import { Box, Flex, FlexItem, Tabs } from '@volue/wave-react';
import React from 'react';

import {
  getAttentionRequiringGridElementsByLoadFlow,
  getAttentionRequiringGridElementsByShortCircuit
} from '@/features/execute-calculation-set/overmind/calculation/actions';
import { useActions, useAppState } from '@/overmind';

import { getGridElementsWithIssuesRequiringAttentionText } from '../../overmind/calculation/evaluation/attentionRequiredMessages';
import {
  anyLoadFlowElementRequiresAttention,
  countLoadFlowGridElementsRequiringAttention,
  countLoadFlowIssuesRequiringAttention
} from '../../overmind/calculation/evaluation/loadFlow/attentionRequired';
import {
  anyShortCircuitElementRequiresAttention,
  countShortCircuitGridElementsRequiringAttention,
  countShortCircuitIssuesRequiringAttention
} from '../../overmind/calculation/evaluation/shortCircuit/attentionRequired';
import { AllTacticResults } from './AllTacticResults';
import { CollapsibleCard } from './CollapsibleCard';
import { GridElementsTree } from './GridElementsTree';
import { InlineNotification } from './InlineNotification';
import { LoadFlowResults } from './LoadFlowResults';
import { ShortCircuitResults } from './ShortCircuitResults';

export const CalculationTacticResults = () => {
  const calculationResult =
    useAppState().calculation.calculationTactic.matches('LOADED')?.value;
  const loadFlowSummary = useAppState().calculation.loadFlowSummary;
  const shortCircuitSummary = useAppState().calculation.shortCircuitSummary;
  const evaluatedGridElements =
    useAppState().calculation.gridElementsDetailsByCalculationType;
  const gridElementsSummary =
    useAppState().calculation.gridElementsAttentionSummary;
  const handleGridElementSelected =
    useActions().calculation.handleGridElementSelected;
  const attentionStatuses =
    useAppState().calculation.gridElementsAttentionStatuses;
  const isEvaluated =
    evaluatedGridElements.type === 'EVALUATED' &&
    attentionStatuses.type === 'AVAILABLE';

  if (
    !calculationResult ||
    (calculationResult && calculationResult.status === 'Failed')
  )
    return null;

  return (
    <>
      <Tabs.Content value="ALL" className="mAm">
        {loadFlowSummary.type === 'SUMMARY_AVAILABLE' &&
          shortCircuitSummary.type === 'SUMMARY_AVAILABLE' && (
            <AllTacticResults
              calculationTacticResult={calculationResult}
              loadFlowSummary={loadFlowSummary.summary}
              shortCircuitSummary={shortCircuitSummary.summary}
            />
          )}
      </Tabs.Content>
      <Tabs.Content value="ALL_LOAD_FLOW" className="mAm">
        <Flex className="w100" flow={'column'}>
          <FlexItem flex={'auto'} css={{ marginTop: '$spacingL' }}>
            {isEvaluated &&
              anyLoadFlowElementRequiresAttention(
                attentionStatuses.byCalculationType.loadFlow
              ) && (
                <InlineNotification
                  type="WARNING"
                  content={getGridElementsWithIssuesRequiringAttentionText(
                    countLoadFlowGridElementsRequiringAttention(
                      attentionStatuses.byCalculationType
                    ),
                    countLoadFlowIssuesRequiringAttention(
                      attentionStatuses.byCalculationType
                    )
                  )}
                />
              )}
          </FlexItem>
          <CollapsibleCard
            expandedByDefault
            header={'Summary'}
            content={
              loadFlowSummary.type === 'SUMMARY_AVAILABLE' && (
                <LoadFlowResults
                  loadFlowSummary={loadFlowSummary.summary}
                  loadFlowScenarios={
                    calculationResult.calculationSetResult.calculationScenarios
                      .loadFlow
                  }
                />
              )
            }
          />
          <FlexItem flex="auto">
            <Flex flow={'column'} className="mHxl">
              <Box
                css={{
                  color: '$colorAccent70',
                  fontWeight: 'bolder',
                  fontSize: 'larger'
                }}
              >
                {'Elements that require attention'}
              </Box>
              {evaluatedGridElements.type === 'EVALUATED' &&
                gridElementsSummary.type ===
                  'GRID_ELEMENTS_SUMMARY_AVAILABLE' && (
                  <FlexItem flex="auto">
                    <GridElementsTree
                      sections={getAttentionRequiringGridElementsByLoadFlow(
                        evaluatedGridElements.gridElementsDetails.loadFlow,
                        gridElementsSummary.summary
                      )}
                      onGridElementClicked={handleGridElementSelected}
                    />
                  </FlexItem>
                )}
            </Flex>
          </FlexItem>
        </Flex>
      </Tabs.Content>
      <Tabs.Content value="SELECTED_LOAD_FLOW" className="mAm">
        <Flex className="w100" flow={'column'}>
          <FlexItem flex={'auto'} css={{ marginTop: '$spacingL' }}>
            {isEvaluated &&
              anyLoadFlowElementRequiresAttention(
                attentionStatuses.byCalculationType.loadFlow
              ) && (
                <InlineNotification
                  type="WARNING"
                  content={getGridElementsWithIssuesRequiringAttentionText(
                    countLoadFlowGridElementsRequiringAttention(
                      attentionStatuses.byCalculationType
                    ),
                    countLoadFlowIssuesRequiringAttention(
                      attentionStatuses.byCalculationType
                    )
                  )}
                />
              )}
          </FlexItem>
          <CollapsibleCard
            expandedByDefault
            header={'Summary'}
            content={
              loadFlowSummary.type === 'SUMMARY_AVAILABLE' && (
                <LoadFlowResults
                  loadFlowSummary={loadFlowSummary.summary}
                  loadFlowScenarios={
                    calculationResult.calculationSetResult.calculationScenarios
                      .loadFlow
                  }
                />
              )
            }
          />
          <FlexItem flex="auto">
            <Flex flow={'column'} className="mHxl">
              <Box
                css={{
                  color: '$colorAccent70',
                  fontWeight: 'bolder',
                  fontSize: 'larger'
                }}
              >
                {'Elements that require attention'}
              </Box>
              {evaluatedGridElements.type === 'EVALUATED' &&
                gridElementsSummary.type ===
                  'GRID_ELEMENTS_SUMMARY_AVAILABLE' && (
                  <FlexItem flex="auto">
                    <GridElementsTree
                      sections={getAttentionRequiringGridElementsByLoadFlow(
                        evaluatedGridElements.gridElementsDetails.loadFlow,
                        gridElementsSummary.summary
                      )}
                      onGridElementClicked={handleGridElementSelected}
                    />
                  </FlexItem>
                )}
            </Flex>
          </FlexItem>
        </Flex>
      </Tabs.Content>
      <Tabs.Content value="SHORT_CIRCUIT" className="mAm">
        <Flex className="w100" flow={'column'}>
          <FlexItem flex={'auto'} css={{ marginTop: '$spacingL' }}>
            {isEvaluated &&
              anyShortCircuitElementRequiresAttention(
                attentionStatuses.byCalculationType.shortCircuit
              ) && (
                <InlineNotification
                  type="WARNING"
                  content={getGridElementsWithIssuesRequiringAttentionText(
                    countShortCircuitGridElementsRequiringAttention(
                      attentionStatuses.byCalculationType
                    ),
                    countShortCircuitIssuesRequiringAttention(
                      attentionStatuses.byCalculationType
                    )
                  )}
                />
              )}
          </FlexItem>
          <CollapsibleCard
            expandedByDefault
            header={'Summary'}
            content={
              shortCircuitSummary.type === 'SUMMARY_AVAILABLE' && (
                <ShortCircuitResults
                  shortCircuitSummary={shortCircuitSummary.summary}
                  shortCircuitScenarios={
                    calculationResult.calculationSetResult.calculationScenarios
                      .shortCircuit
                  }
                />
              )
            }
          />
          <FlexItem flex="auto">
            <Flex flow={'column'} className="mHxl">
              <Box
                css={{
                  color: '$colorAccent70',
                  fontWeight: 'bolder',
                  fontSize: 'larger'
                }}
              >
                {'Elements that require attention'}
              </Box>
              {evaluatedGridElements.type === 'EVALUATED' &&
                gridElementsSummary.type ===
                  'GRID_ELEMENTS_SUMMARY_AVAILABLE' && (
                  <FlexItem flex="auto">
                    <GridElementsTree
                      sections={getAttentionRequiringGridElementsByShortCircuit(
                        evaluatedGridElements.gridElementsDetails.shortCircuit,
                        gridElementsSummary.summary
                      )}
                      onGridElementClicked={handleGridElementSelected}
                    />
                  </FlexItem>
                )}
            </Flex>
          </FlexItem>
        </Flex>
      </Tabs.Content>
    </>
  );
};
